import React, {useCallback, useState} from 'react';
import {useParams} from 'react-router';
import {Button, CircularProgress, Divider, IconButton, Stack, TextField, Tooltip, Typography,} from '@mui/material';
import {Add as AddIcon, ContentCopy as CopyIcon, Psychology} from '@mui/icons-material';
import {useReportStore} from '../../../useReportStore';
import {generateAlternativeConclusions} from '../../../../../utils/api/aiCitationAnalysis';
import {createBlockNode, createHeading} from '../../../../../components/prosemirror-editor/helpers/nodeCreation';

/**
 * AlternativeHypotheses component for AI Assistant
 * Generates alternative hypotheses/conclusions for the report and displays them in the panel
 */
const AlternativeHypotheses = () => {
    const {reportId} = useParams();
    const editorView = useReportStore(s => s.editorView);

    const [instruction, setInstruction] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [alternatives, setAlternatives] = useState(null);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /**
     * Get document content from editor
     */
    const getDocumentContent = useCallback(() => {
        if (!editorView) return null;

        // If it has a TipTap editor reference
        if (editorView.editor) {
            return editorView.editor.getJSON();
        }

        // Otherwise work directly with the ProseMirror view
        return editorView.state?.doc?.toJSON();
    }, [editorView]);

    /**
     * Handle generate button click
     */
    const handleGenerate = useCallback(async () => {
        if (!reportId || isGenerating) return;

        const content = getDocumentContent();
        if (!content) {
            setError('Editor not available or could not get document content');
            return;
        }

        setIsGenerating(true);
        setError(null);
        setAlternatives(null);

        try {
            console.log('🎯 Generating alternatives with instruction:', instruction);
            const result = await generateAlternativeConclusions(
                reportId,
                content,
                instruction || undefined
            );

            console.log('✅ Received alternatives:', result);

            if (result.alternatives && result.alternatives.length > 0) {
                setAlternatives(result.alternatives);
            } else {
                console.warn('No alternatives generated');
                setError('No alternatives were generated');
            }

        } catch (err) {
            console.error('❌ Error generating alternatives:', err);
            setError(err.message || 'Failed to generate alternatives');
        } finally {
            setIsGenerating(false);
        }
    }, [reportId, isGenerating, instruction, getDocumentContent]);

    /**
     * Copy alternative text to clipboard as plain text
     */
    const handleCopyText = useCallback((alt, index) => {
        // Format: title + text
        const formattedText = `${alt.perspective}\n${alt.text}`;

        navigator.clipboard.writeText(formattedText)
            .then(() => {
                console.log('✅ Text copied to clipboard');
                setCopiedIndex(index);
                setTimeout(() => setCopiedIndex(null), 2000);
            })
            .catch(err => {
                console.error('❌ Failed to copy text:', err);
            });
    }, []);

    /**
     * Find or create "ALTERNATIVE HYPOTHESES" heading and insert all data there
     */
    const handleAddToDocument = useCallback((alt) => {
        if (!editorView) {
            console.error('No editor view available');
            return;
        }

        const {state, dispatch} = editorView;
        const {schema, doc} = state;
        const authorMark = schema.marks.author;

        if (!authorMark) {
            console.error('Author mark type not found in schema');
            return;
        }

        // Create author mark for AI
        const aiAuthorMark = authorMark.create({userId: 'ai'});

        // Find "ALTERNATIVE HYPOTHESES" heading at top level and determine its level
        let alternativeHypothesesPos = null;
        let alternativeHypothesesLevel = null;
        let insertPos = null;

        doc.descendants((node, pos, parent) => {
            // Only check top-level nodes (direct children of doc)
            if (parent !== doc) return true;

            if (node.type.name === 'heading' && node.textContent === 'ALTERNATIVE HYPOTHESES') {
                alternativeHypothesesPos = pos;
                alternativeHypothesesLevel = node.attrs.level;
                return true; // Continue to find insert position
            }

            // If we found the target heading, look for the end of its section
            if (alternativeHypothesesPos !== null && insertPos === null) {
                // Check if this is a heading of same or higher level (lower number)
                if (node.type.name === 'heading' && node.attrs.level <= alternativeHypothesesLevel) {
                    insertPos = pos;
                    return false; // Stop searching
                }
            }

            return true;
        });

        // If we found the section but no next heading, insert at end
        if (alternativeHypothesesPos !== null && insertPos === null) {
            insertPos = doc.content.size;
        }

        let tr = state.tr;
        let scrollToPos = insertPos; // Position to scroll to after insertion

        // If "ALTERNATIVE HYPOTHESES" heading doesn't exist, create it at the end
        if (alternativeHypothesesPos === null) {
            const sectionHeading = createHeading(
                schema,
                2,
                null,
                schema.text('ALTERNATIVE HYPOTHESES', [aiAuthorMark])
            );

            alternativeHypothesesLevel = 2;
            insertPos = doc.content.size;

            // Create nodes for this alternative
            const subHeading = createHeading(
                schema,
                alternativeHypothesesLevel + 1,
                null,
                schema.text(alt.perspective, [aiAuthorMark])
            );

            const textParagraph = createBlockNode(
                schema.nodes.paragraph,
                null,
                schema.text(alt.text, [aiAuthorMark])
            );

            tr = tr.insert(insertPos, [sectionHeading, subHeading, textParagraph]);
            // Scroll to the subheading (after section heading)
            scrollToPos = insertPos + sectionHeading.nodeSize;
        } else {
            // Create nodes for this alternative (one level lower than "ALTERNATIVE HYPOTHESES")
            const subHeading = createHeading(
                schema,
                alternativeHypothesesLevel + 1,
                null,
                schema.text(alt.perspective, [aiAuthorMark])
            );

            const textParagraph = createBlockNode(
                schema.nodes.paragraph,
                null,
                schema.text(alt.text, [aiAuthorMark])
            );

            tr = tr.insert(insertPos, [subHeading, textParagraph]);
            // Scroll to the inserted subheading
            scrollToPos = insertPos;
        }

        // Mark transaction to skip author plugin (we've already set author marks manually)
        tr = tr.setMeta('skipAuthorPlugin', true);

        // Set the selection to the inserted content position
        tr = tr.setSelection(state.selection.constructor.near(tr.doc.resolve(scrollToPos)));

        dispatch(tr);
        console.log('✅ Transaction dispatched, document updated');

        // Scroll to the inserted content smoothly and centered
        // Use setTimeout to ensure DOM is fully updated (similar to taskHelpers.js)
        setTimeout(() => {
            try {
                // Use the current selection from the updated editor state
                const currentState = editorView.state;
                const selectionPos = currentState.selection.from;

                console.log('📜 Scrolling to position:', selectionPos);

                const domAtPos = editorView.domAtPos(selectionPos);
                console.log('📜 DOM at position:', domAtPos);

                if (domAtPos && domAtPos.node) {
                    // Find the closest block-level element
                    let targetElement = domAtPos.node.nodeType === Node.ELEMENT_NODE
                        ? domAtPos.node
                        : domAtPos.node.parentElement;

                    // Walk up to find an element with data-block-id or a heading/paragraph
                    while (targetElement && !targetElement.dataset?.blockId &&
                    !['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P'].includes(targetElement.tagName)) {
                        targetElement = targetElement.parentElement;
                    }

                    console.log('📜 Target element for scroll:', targetElement);

                    if (targetElement) {
                        targetElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                        });
                        console.log('📜 scrollIntoView called');
                    }
                }
            } catch (err) {
                console.warn('Could not scroll to position:', err);
            }
            editorView.focus();
        }, 100);
    }, [editorView]);

    return (
        <Stack flexGrow={1} overflow={'auto'}>
            <Stack spacing={3} p={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Psychology color="primary"/>
                    <Typography variant="body2" color="text.secondary">
                        Generate alternative hypotheses and conclusions for your analysis.
                    </Typography>
                </Stack>

                <TextField
                    label="Instructions (optional)"
                    placeholder="E.g., Focus on geopolitical factors, consider economic implications..."
                    multiline
                    rows={3}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    fullWidth
                    disabled={isGenerating}
                />

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerate}
                    disabled={isGenerating || !editorView}
                    startIcon={isGenerating ? <CircularProgress size={20} color="inherit"/> : <Psychology/>}
                >
                    {isGenerating ? 'Generating...' : 'Generate Hypotheses'}
                </Button>

                {error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}

                {!editorView && (
                    <Typography color="text.secondary" variant="body2">
                        Open a report in the editor to generate alternative hypotheses.
                    </Typography>
                )}
            </Stack>

            {/* Render generated alternatives */}
            {alternatives && alternatives.length > 0 && (
                <Stack>
                    <Divider/>
                    <Typography p={2} variant="subtitle2" fontWeight="bold">
                        Generated Alternatives
                    </Typography>
                    <Stack px={2} spacing={2}>
                        {alternatives.map((alt, idx) => (
                            <Stack key={idx} spacing={1}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="subtitle2" color="primary">
                                        Alternative {idx + 1}: {alt.perspective}
                                    </Typography>
                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title={copiedIndex === idx ? "Copied!" : "Copy text"}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCopyText(alt, idx)}
                                                color={copiedIndex === idx ? "success" : "primary"}
                                            >
                                                <CopyIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Add to document">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleAddToDocument(alt)}
                                                color="primary"
                                                disabled={!editorView}
                                            >
                                                <AddIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                                <Typography variant="body2" sx={{mb: 1}}>
                                    {alt.text}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    <em>Rationale:</em> {alt.rationale}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export default AlternativeHypotheses;
